import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { referralCodeAPI } from '../services/api';

function SendReferralCodeEmails() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [progress, setProgress] = useState({ total: 0, sent: 0, failed: 0 });
  const [result, setResult] = useState(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    loadRecipients();
    return () => {
      pollingRef.current = false;
    };
  }, []);

  const loadRecipients = async () => {
    setLoading(true);
    try {
      const response = await referralCodeAPI.getRecipients();
      const data = response.data.data || [];
      setRecipients(data);
    } catch (error) {
      toast.error(error.message || 'Failed to load recruiters');
    } finally {
      setLoading(false);
    }
  };

  const filteredRecipients = recipients.filter(r => {
    const q = search.toLowerCase();
    return (
      !q ||
      (r.full_name && r.full_name.toLowerCase().includes(q)) ||
      (r.email && r.email.toLowerCase().includes(q)) ||
      (r.referral_code && r.referral_code.toLowerCase().includes(q))
    );
  });

  const toggleSelectAll = () => {
    const next = !selectAll;
    setSelectAll(next);
    const updated = { ...selected };
    filteredRecipients.forEach(r => {
      if (next) updated[r.id] = true;
      else delete updated[r.id];
    });
    setSelected(updated);
  };

  const toggleRecipient = (id) => {
    setSelected(prev => {
      const updated = { ...prev };
      if (updated[id]) delete updated[id];
      else updated[id] = true;
      return updated;
    });
  };

  const selectedIds = Object.keys(selected).filter(id => selected[id]).map(Number);
  const selectedCount = selectedIds.length;

  const handleDryRun = async () => {
    if (selectedCount === 0) {
      toast.error('Please select at least one recruiter or choose "Select all"');
      return;
    }
    setPreviewing(true);
    setResult(null);
    try {
      const response = await referralCodeAPI.sendCodeChangeEmails({
        recruiters: selectedIds,
        dryRun: true
      });
      setResult({ ...response.data, preview: true });
    } catch (error) {
      toast.error(error.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const pollProgress = async (jobId) => {
    pollingRef.current = true;
    while (pollingRef.current) {
      try {
        const resp = await referralCodeAPI.getSendProgress(jobId);
        const p = resp.data;
        setProgress({ total: p.total, sent: p.sent, failed: p.failed });
        if (p.done) {
          pollingRef.current = false;
          setSending(false);
          setResult({
            preview: false,
            sentCount: p.sent,
            failedCount: p.failed,
            sent: p.sentList,
            failed: p.failedList
          });
          if (p.sent > 0) {
            toast.success(`Emails sent to ${p.sent} recruiter(s)`);
          }
          if (p.failed > 0) {
            toast.warning(`${p.failed} email(s) failed to send`);
          }
          return;
        }
      } catch (error) {
        pollingRef.current = false;
        setSending(false);
        toast.error(error.message || 'Failed to check email progress');
        return;
      }
      await new Promise(r => setTimeout(r, 1000));
    }
  };

  const handleSendAll = async () => {
    if (selectedCount === 0) {
      toast.error('Please select at least one recruiter or choose "Select all"');
      return;
    }
    const confirmText = `Send referral code change emails to ${selectedCount} recruiter(s)?`;
    if (!window.confirm(confirmText)) return;

    setSending(true);
    setResult(null);
    setProgress({ total: 0, sent: 0, failed: 0 });
    try {
      const response = await referralCodeAPI.sendCodeChangeEmails({
        recruiters: selectedIds,
        dryRun: false
      });
      setProgress({ total: response.data.total, sent: 0, failed: 0 });
      pollProgress(response.data.jobId);
    } catch (error) {
      setSending(false);
      toast.error(error.message || 'Failed to send emails');
    }
  };

  const selectableCount = filteredRecipients.length;
  const progressPct = progress.total > 0 ? Math.round(((progress.sent + progress.failed) / progress.total) * 100) : 0;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h2 className="page-title">Referral Code Change Emails</h2>
          <p className="page-subtitle">Send an email to recruiters telling them their referral code has been updated.</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-outline" onClick={loadRecipients} disabled={loading || sending || previewing}>
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Select Recipients</h3>
          <span className="badge badge-info">{selectedCount} / {recipients.length} selected</span>
        </div>
        <div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Search by name, email or referral code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: '220px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={selectAll}
                onChange={toggleSelectAll}
                disabled={loading}
              />
              Select all ({selectableCount} shown)
            </label>
          </div>

          {loading ? (
            <div className="loading-state">Loading recruiters...</div>
          ) : filteredRecipients.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>No recruiters found.</div>
          ) : (
            <div style={{ maxHeight: '480px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Recruiter</th>
                    <th>Email</th>
                    <th>Initial Code</th>
                    <th>New Code</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecipients.map(r => (
                    <tr
                      key={r.id}
                      onClick={() => toggleRecipient(r.id)}
                      style={{ cursor: 'pointer', background: selected[r.id] ? '#f0f9ff' : undefined }}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={!!selected[r.id]}
                          onChange={() => toggleRecipient(r.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{r.full_name}</td>
                      <td>{r.email}</td>
                      <td>
                        <span className="badge badge-secondary">{r.initial_code || '—'}</span>
                      </td>
                      <td>
                        <span className="badge badge-info">{r.referral_code}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Send Emails</h3>
        </div>
        <div>
          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
            Each selected recruiter will receive an email with their new 4-digit referral code. Use "Preview" first to verify the recipient list before sending.
          </p>
          {sending && progress.total > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px', color: '#374151' }}>
                <span>Sending emails... {progress.sent + progress.failed} / {progress.total}</span>
                <span style={{ fontWeight: 600 }}>{progressPct}%</span>
              </div>
              <div style={{ background: '#e5e7eb', borderRadius: '8px', height: '16px', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${progressPct}%`,
                    background: progress.failed > 0
                      ? 'linear-gradient(90deg, #10b981 0%, #f59e0b 100%)'
                      : 'linear-gradient(90deg, #037b90 0%, #10b981 100%)',
                    height: '100%',
                    borderRadius: '8px',
                    transition: 'width 0.4s ease'
                  }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                ✅ {progress.sent} sent • ❌ {progress.failed} failed
              </div>
            </div>
          )}
          <div className="page-header-actions" style={{ justifyContent: 'flex-start' }}>
            <button className="btn btn-secondary" onClick={handleDryRun} disabled={loading || sending || previewing || selectedCount === 0}>
              {previewing ? 'Previewing...' : '👁 Preview'}
            </button>
            <button className="btn btn-primary" onClick={handleSendAll} disabled={loading || sending || previewing || selectedCount === 0}>
              {sending ? `Sending... ${progressPct}%` : '📨 Send Emails'}
            </button>
          </div>
        </div>
      </div>

      {result && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              {result.preview ? '👁 Preview Results' : '📨 Email Results'}
            </h3>
            {result.preview ? (
              <span className="badge badge-info">{result.recipientsCount} recipient(s) would receive the email</span>
            ) : (
              <span className="badge badge-success">{result.sentCount} sent, {result.failedCount} failed</span>
            )}
          </div>
          <div>
            {result.preview ? (
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Recruiter</th>
                      <th>Email</th>
                      <th>Initial Code</th>
                      <th>New Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.recipients || []).map(r => (
                      <tr key={r.id}>
                        <td>{r.full_name}</td>
                        <td>{r.email}</td>
                        <td><span className="badge badge-secondary">{r.initial_code || '—'}</span></td>
                        <td><span className="badge badge-info">{r.referral_code}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '280px' }}>
                  <h4 style={{ color: '#059669', marginBottom: '10px', fontSize: '15px' }}>✅ Sent ({result.sentCount})</h4>
                  <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#374151' }}>
                    {result.sent.length === 0 ? <p>None</p> : result.sent.map((r, i) => (
                      <div key={i} style={{ padding: '4px 0', borderBottom: i < result.sent.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <strong>{r.full_name}</strong> — {r.email}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: '280px' }}>
                  <h4 style={{ color: '#dc2626', marginBottom: '10px', fontSize: '15px' }}>❌ Failed ({result.failedCount})</h4>
                  <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#7f1d1d' }}>
                    {result.failed.length === 0 ? <p>None</p> : result.failed.map((r, i) => (
                      <div key={i} style={{ padding: '4px 0', borderBottom: i < result.failed.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <strong>{r.full_name}</strong> — {r.email}
                        {r.error && <div style={{ fontSize: '12px', color: '#b91c1c' }}>{r.error}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SendReferralCodeEmails;
